import {useQuery} from '@tanstack/react-query';
import type {Geometry} from 'geojson';
import {DateTime} from 'luxon';
import urls from './urls.ts';
import {request} from './index.js';
import {
  API_PROCESS_FILE_IDENTIFIER_TYPES,
  API_PROCESS_OUTPUT_FORMATS,
  API_VERSION,
  ApiProcessFileIdentifierType,
  ApiProcessOutputFormatType,
  ApiVersionType,
  INTERPOLATOR,
  InterpolatorValue,
  MOSAICKING_ORDER,
  MosaickingOrderValue,
} from '@src/constants/analyze.ts';

type EvalscriptParams = Partial<{
  buffer: number;
  blockSize: number;
}>;

export type ProcessRequestParameters = {
  evalscript: string;
  collectionId: string;
  geometry: Geometry;
  width?: number;
  height?: number;
  from?: string;
  to?: string;
  identifier?: ApiProcessFileIdentifierType;
  format?: ApiProcessOutputFormatType;
  mosaickingOrder?: MosaickingOrderValue;
  downsampling?: InterpolatorValue;
  upsampling?: InterpolatorValue;
  evalscriptParams?: EvalscriptParams;
};

export type ProcessRequestBody = {
  evalscript: string;
  evalscriptParams?: EvalscriptParams;
  input: {
    bounds: {
      properties: {
        crs:
          | 'http://www.opengis.net/def/crs/EPSG/0/4326'
          | 'http://www.opengis.net/def/crs/EPSG/0/3857';
      };
    } & ({bbox: [number, number, number, number]} | {geometry: Geometry});
    data: {
      type: string;
      id?: string;
      dataFilter: {
        mosaickingOrder?: MosaickingOrderValue;
        timeRange: {
          from: string;
          to: string;
        };
      };
      processing?: {
        downsampling?: InterpolatorValue;
        upsampling?: InterpolatorValue;
      };
    }[];
  };
  output: {
    responses: {
      identifier: string;
      format: {
        type: string;
      };
    }[];
  } & ({width: number; height: number} | {resx: number; resy: number});
};

const createProcessRequestBody = ({
  evalscript,
  collectionId,
  geometry,
  width = 100,
  height = 100,
  from = DateTime.now().startOf('day').toISO(),
  to = DateTime.now().endOf('day').toISO(),
  mosaickingOrder = MOSAICKING_ORDER.mostRecent,
  downsampling = INTERPOLATOR.bilinear,
  upsampling = INTERPOLATOR.bilinear,
  identifier = API_PROCESS_FILE_IDENTIFIER_TYPES.default,
  format = API_PROCESS_OUTPUT_FORMATS.png,
  evalscriptParams,
}: ProcessRequestParameters): ProcessRequestBody => {
  const interpolatedBody: ProcessRequestBody = {
    evalscript,
    ...(evalscriptParams ? {evalscriptParams} : {}),
    input: {
      bounds: {
        properties: {crs: 'http://www.opengis.net/def/crs/EPSG/0/4326'},
        geometry,
      },
      data: [
        {
          dataFilter: {
            mosaickingOrder,
            timeRange: {from, to},
          },
          type: collectionId,
          ...(downsampling || upsampling
            ? {
                processing: {
                  downsampling,
                  upsampling,
                },
              }
            : {}),
        },
      ],
    },
    output: {
      width,
      height,
      responses: [
        {
          identifier,
          format: {
            type: format,
          },
        },
      ],
    },
  };
  return interpolatedBody;
};

const getProcessImage = async (
  body: ProcessRequestBody,
  format: ApiProcessOutputFormatType = API_PROCESS_OUTPUT_FORMATS.png,
  version: ApiVersionType = API_VERSION.v1,
): Promise<Blob> => {
  const endpoint = version === API_VERSION.v2 ? urls.processV2 : urls.process;
  const response = await request.post(endpoint, body, {
    headers: {
      'Content-Type': 'application/json',
      Accept: format,
    },
    responseType: 'blob',
  });
  return await response.data;
};

/**
 * Generate a query key for the process image query.
 * This ensures consistent query key generation across components.
 */
export const getProcessImageQueryKey = (
  parameters: Partial<ProcessRequestParameters>,
  version: ApiVersionType = API_VERSION.v1,
) => {
  return Object.entries({...parameters, version});
};

export const useGetProcessImage = (
  parameters: Partial<ProcessRequestParameters>,
  options?: {
    enabled?: boolean;
    version?: ApiVersionType;
  },
) => {
  const {evalscript, collectionId, geometry, format} = parameters;
  const {enabled = true, version = API_VERSION.v1} = options || {};
  return useQuery({
    queryKey: getProcessImageQueryKey(parameters, version),
    queryFn: () => {
      const requestBody = createProcessRequestBody(
        parameters as ProcessRequestParameters,
      );
      return getProcessImage(requestBody, format, version);
    },
    enabled:
      enabled &&
      Boolean(evalscript) &&
      Boolean(collectionId) &&
      Boolean(geometry),
    staleTime: Infinity,
  });
};
